import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { UsernatorPromiseClient } from "../generated/user_grpc_web_pb";
import { LoginRequest, UserData as UserDataMessage } from "../generated/user_pb";
import GRPC_BACKEND_URL from "../grpcConfig";
import { setAuthCookie, getAuthCookie, clearAuthCookie, CookieUser, hashStringSHA256, isNetworkError } from "@dowty/core";
import { getSsoIssuerBaseUrl, isSsoProducerEnabled } from "../ssoConfig";

const ISSUER_REQUEST_TIMEOUT_MS = 3000;
const ISSUER_SIGNED_OUT_MESSAGE = "Issuer session was signed out";

const postIssuerRequest = async (url: string, body?: string): Promise<void> => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), ISSUER_REQUEST_TIMEOUT_MS);
    try {
        const response = await fetch(url, {
            method: "POST",
            credentials: "include",
            signal: controller.signal,
            ...(body ? { headers: { "Content-Type": "application/json" }, body } : {}),
        });
        if (response.status === 401 && typeof response.json === "function") {
            const payload = await response.json().catch(() => null);
            if (payload?.error === "signed_out") throw new Error(ISSUER_SIGNED_OUT_MESSAGE);
        }
        if (!response.ok) throw new Error("Issuer request failed");
    } finally {
        window.clearTimeout(timeout);
    }
};

export type AuthUser = {
    userId: number;
    username: string;
    token: string;
    roles: string[];
};

type AuthContextValue = {
    user: AuthUser | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (username: string, password: string) => Promise<AuthUser>;
    establishIssuerSession: (sessionUser: AuthUser) => Promise<void>;
    register: (username: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    // Applies a server-confirmed change (e.g. from updateUserInfo) to the in-memory user without
    // a network round-trip. Keeps the nav, avatar and the dowtex-auth cookie in sync with the
    // shared Users row so a rename doesn't keep showing the old name until next login.
    updateUser: (updates: Partial<Pick<AuthUser, "username" | "token">>) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const readStoredUser = (): AuthUser | null => {
    const cookie = getAuthCookie();
    if (cookie?.token) return { roles: [], ...cookie };
    return null;
};

const mapUser = (message: UserDataMessage): AuthUser => {
    const token = message.getToken();
    if (!token) throw new Error("Token missing in response");
    return {
        userId: message.getUserid(),
        username: message.getUsername(),
        token,
        roles: message.getRolesList(),
    };
};

const buildUserMessage = (user: AuthUser): UserDataMessage => {
    const msg = new UserDataMessage();
    msg.setUserid(user.userId);
    msg.setUsername(user.username);
    msg.setToken(user.token);
    return msg;
};

const userClient = new UsernatorPromiseClient(GRPC_BACKEND_URL, null, null);

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const storedUserRef = useRef<AuthUser | null>(readStoredUser());
    const [user, setUser] = useState<AuthUser | null>(storedUserRef.current);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const hasValidatedInitialUser = useRef<boolean>(false);
    const sessionGeneration = useRef<number>(0);
    const issuerSessionRequest = useRef<Promise<void> | null>(null);

    // LoginPage can await this before an issuer redirect; regular portal auth stays independent of optional SSO.
    const establishIssuerSession = useCallback((sessionUser: AuthUser): Promise<void> => {
        const issuerUrl = getSsoIssuerBaseUrl();
        if (!isSsoProducerEnabled() || !issuerUrl) return Promise.reject(new Error("SSO issuer is unavailable"));
        if (issuerSessionRequest.current) return issuerSessionRequest.current;
        const request: Promise<void> = postIssuerRequest(
            `${issuerUrl}/sso/session`,
            JSON.stringify({ token: sessionUser.token }),
        ).catch(error => {
            // A failed attempt must not stick: the next redirect or login retries.
            if (issuerSessionRequest.current === request) issuerSessionRequest.current = null;
            throw error;
        });
        issuerSessionRequest.current = request;
        return request;
    }, []);

    // Keep the shared cookie writer through the seven-day dual-run; remove it at step 5, "Retirement", in docs/sso-producer-migration.md.
    useEffect(() => {
        if (!user) {
            clearAuthCookie();
            return;
        }
        setAuthCookie(user as CookieUser);
    }, [user]);

    // Validate existing session on mount
    useEffect(() => {
        if (!storedUserRef.current || hasValidatedInitialUser.current) return;

        hasValidatedInitialUser.current = true;

        const validateExistingSession = async () => {
            const generation = sessionGeneration.current;
            try {
                const response = await userClient.validateSession(buildUserMessage(storedUserRef.current!), {});
                if (generation !== sessionGeneration.current) return;
                const refreshed = mapUser(response);
                void establishIssuerSession(refreshed).catch(error => {
                    if (generation === sessionGeneration.current && error instanceof Error && error.message === ISSUER_SIGNED_OUT_MESSAGE) setUser(null);
                });
                setUser(refreshed);
            } catch (error) {
                if (generation !== sessionGeneration.current) return;
                // Only a genuine rejection clears the session. A connectivity failure must not:
                // this runs on every startup, so an unconditional setUser(null) signed the user out
                // the moment they opened the installed app offline - and every authenticated screen
                // then bounced to login before the offline caches beneath them could render. An
                // app that logs you out for being offline has no offline mode.
                if (isNetworkError(error)) {
                    console.warn("Session validation could not reach the server; keeping the stored session", error);
                    setUser(storedUserRef.current);
                } else {
                    console.warn("Session validation failed, clearing auth", error);
                    setUser(null);
                }
            } finally {
                storedUserRef.current = null;
            }
        };

        validateExistingSession();
    }, [establishIssuerSession]);

    const login = useCallback(async (username: string, password: string): Promise<AuthUser> => {
        setIsLoading(true);
        try {
            const request = new LoginRequest();
            request.setUsername(username);
            const hashedPassword = await hashStringSHA256(password);
            request.setPassword(hashedPassword);
            const response = await userClient.login(request, {});
            const authenticated = mapUser(response);
            sessionGeneration.current += 1;
            issuerSessionRequest.current = null;
            void establishIssuerSession(authenticated).catch(() => undefined);
            setUser(authenticated);
            return authenticated;
        } finally {
            setIsLoading(false);
        }
    }, [establishIssuerSession]);

    const register = useCallback(async (username: string, password: string) => {
        setIsLoading(true);
        try {
            const request = new LoginRequest();
            request.setUsername(username);
            const hashedPassword = await hashStringSHA256(password);
            request.setPassword(hashedPassword);
            await userClient.register(request, {});
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logout = useCallback(async () => {
        const issuerUrl = getSsoIssuerBaseUrl();
        if (isSsoProducerEnabled() && issuerUrl) {
            if (issuerSessionRequest.current) await issuerSessionRequest.current.catch(() => undefined);
            try {
                await postIssuerRequest(`${issuerUrl}/sso/logout`);
            } catch (error) {
                if (!(error instanceof Error) || error.message !== ISSUER_SIGNED_OUT_MESSAGE) throw error;
            }
        }
        sessionGeneration.current += 1;
        issuerSessionRequest.current = null;
        setUser(null);
    }, []);

    const updateUser = useCallback((updates: Partial<Pick<AuthUser, "username" | "token">>) => {
        setUser(prev => (prev ? { ...prev, ...updates } : prev));
    }, []);

    const value = useMemo<AuthContextValue>(
        () => ({
            user,
            isAuthenticated: Boolean(user),
            isLoading,
            login,
            establishIssuerSession,
            register,
            logout,
            updateUser,
        }),
        [isLoading, login, establishIssuerSession, register, logout, updateUser, user]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within an AuthProvider");
    return context;
};
